import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { LocalNotifications } from '@capacitor/local-notifications';
import { useAppDispatch, useAppSelector } from 'common/hooks/state';
import { RootState } from 'common/store/store';
import { safeStorage } from 'common/utils/safeStorage';
import { initPushRegistration } from 'common/utils/pushRegistration';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';
import { useShepherd } from 'features/Shepherd/ShepherdProvider';
import {
  MK3_COLORS, bodyStyle, cardShellStyle, headlineStyle, primaryButtonStyle,
} from 'features/Onboarding/components/mk3/styles';
import appIcon from 'assets/images/logo/app-icon-120.png';
import {
  REMINDER_PRIMER_EVENT, PrimerEventDetail, getPrimerState, primerAskedKey, primerCopyForEvent, primerPreviewForEvent,
  setPrimerOpen, shouldOfferPrimer,
} from './primer';

interface CardProps {
  title: string;
  body: string;
  /** A mock notification showing the kind of reminder they will get. */
  preview?: { title: string; body: string };
  onAllow: () => void;
  onNotNow: () => void;
}

const SYSTEM_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

/** Looks like a lock-screen notification, so people see what they are allowing. */
const NotificationPreview = ({ title, body }: { title: string; body: string }) => (
  <div
    aria-hidden
    className="flex items-start gap-3 text-left mb-5"
    style={{
      background: '#FFFFFF',
      borderRadius: 18,
      padding: '10px 12px',
      boxShadow: '0 6px 18px rgba(57, 38, 79, 0.14)',
      border: '1px solid #EEE8F3',
      fontFamily: SYSTEM_FONT,
    }}
    data-cy="notification-primer-preview"
  >
    <img src={appIcon} alt="" width={36} height={36} style={{ borderRadius: 8, flexShrink: 0 }} />
    <div className="flex-1 min-w-0">
      <div className="flex justify-between" style={{ fontSize: 12, color: '#8A8291' }}>
        <span style={{ fontWeight: 600, letterSpacing: 0.2 }}>DOQIT</span>
        <span>now</span>
      </div>
      <div
        style={{
          fontSize: 15, fontWeight: 600, color: '#1C1622', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 14, color: '#3A3340', lineHeight: '18px' }}>{body}</div>
    </div>
  </div>
);

export const NotificationPrimerCard = ({
  title, body, preview, onAllow, onNotNow,
}: CardProps) => (
  <div
    className="w-full max-w-sm rounded-3xl p-6 text-center"
    style={cardShellStyle}
    role="dialog"
    aria-modal="true"
    aria-label={title}
    data-cy="notification-primer"
  >
    {preview && <NotificationPreview title={preview.title} body={preview.body} />}
    <h2 style={{ ...headlineStyle, textAlign: 'center' }}>{title}</h2>
    <p className="mt-2 mb-5" style={bodyStyle}>{body}</p>
    <button
      type="button"
      onClick={onAllow}
      className="w-full py-4 text-white rounded-full"
      style={primaryButtonStyle}
      data-cy="notification-primer-allow"
    >
      Allow notifications
    </button>
    <button
      type="button"
      onClick={onNotNow}
      className="w-full mt-3 py-3"
      style={{ ...bodyStyle, color: MK3_COLORS.accent }}
      data-cy="notification-primer-not-now"
    >
      Not now
    </button>
  </div>
);

const readAsked = async (key: string): Promise<boolean> => {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value === 'true';
    }
  } catch {
    // fall through to localStorage
  }
  return safeStorage.getItem(key) === 'true';
};

const writeAsked = async (key: string): Promise<void> => {
  safeStorage.setItem(key, 'true');
  try {
    if (Capacitor.isNativePlatform()) await Preferences.set({ key, value: 'true' });
  } catch {
    // best-effort
  }
};

export const NotificationPrimer = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state: RootState) => state.auth.user) as
    { id?: string | number; user?: { id?: string | number } } | null;
  const userId = user?.user?.id ?? user?.id;
  const { activeStepId } = useShepherd();
  const [copy, setCopy] = useState<{ title: string; body: string } | null>(null);
  const [preview, setPreview] = useState<{ title: string; body: string } | undefined>(undefined);
  const showingRef = useRef(false);
  const tourActiveRef = useRef(false);
  tourActiveRef.current = activeStepId !== null;
  // A request that arrived while a tour step was on screen. Held (latest
  // wins) and offered once the tour ends, so the two never stack.
  const heldRef = useRef<PrimerEventDetail | null>(null);

  const close = useCallback(() => {
    showingRef.current = false;
    setCopy(null);
    setPrimerOpen(false);
  }, []);

  const offer = useCallback(async (detail: PrimerEventDetail) => {
    if (userId == null || showingRef.current) return;
    if (tourActiveRef.current) {
      heldRef.current = detail;
      return;
    }
    const permission = await getPrimerState();
    const asked = detail.force ? false : await readAsked(primerAskedKey(userId));
    if (!shouldOfferPrimer(permission, asked) || showingRef.current) return;
    if (tourActiveRef.current) {
      heldRef.current = detail;
      return;
    }
    showingRef.current = true;
    setPrimerOpen(true);
    setCopy(primerCopyForEvent(detail));
    setPreview(primerPreviewForEvent(detail));
    trackEvent(MixpanelEventName.notificationPrimerViewed, { forced: !!detail.force });
  }, [userId]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || userId == null) return undefined;
    heldRef.current = null;
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<PrimerEventDetail>;
      if (detail) offer(detail);
    };
    window.addEventListener(REMINDER_PRIMER_EVENT, handler);
    return () => window.removeEventListener(REMINDER_PRIMER_EVENT, handler);
  }, [userId, offer]);

  // The tour has ended: offer whatever was held back while it ran.
  useEffect(() => {
    if (activeStepId !== null || !heldRef.current) return;
    const held = heldRef.current;
    heldRef.current = null;
    offer(held);
  }, [activeStepId, offer]);

  // Never leave the flag set if this unmounts with the primer showing.
  useEffect(() => () => setPrimerOpen(false), []);

  const handleAllow = async () => {
    close();
    if (userId != null) await writeAsked(primerAskedKey(userId));
    let granted = false;
    try {
      const result = await LocalNotifications.requestPermissions();
      granted = result.display === 'granted';
    } catch {
      granted = false;
    }
    trackEvent(MixpanelEventName.notificationPrimerAllowed, { granted });
    initPushRegistration(dispatch);
  };

  const handleNotNow = async () => {
    close();
    if (userId != null) await writeAsked(primerAskedKey(userId));
    trackEvent(MixpanelEventName.notificationPrimerDeclined);
  };

  if (!copy) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ zIndex: 9980, background: 'rgba(57,38,79,0.45)' }}
    >
      <NotificationPrimerCard
        title={copy.title}
        body={copy.body}
        preview={preview}
        onAllow={handleAllow}
        onNotNow={handleNotNow}
      />
    </div>
  );
};

export default NotificationPrimer;
